/**
 * Risk Assessment Library — Phase 2 (read-only).
 *
 * Two tabs: Blocks and Templates. Both list the CrewForge factory defaults
 * (plus any per-production forks once Phase 3 adds them). Clicking a row
 * expands it inline to show:
 *   - For Blocks: the full hazard table with HRN cells (pre + residual).
 *   - For Templates: the ordered list of blocks the template assembles.
 *
 * No edit affordances yet. Phase 3 wires up the author CRUD.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHistoryState } from '../hooks/useViewInHistory';
import { apiGet, apiPost, apiDelete } from '../utils/api';
import { useDepartment } from '../contexts/DepartmentContext';
import { titleCaseCategory } from '../constants/riskAssessment';
import { BTN_XS } from '../constants/buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import EmptyState from '../components/EmptyState';
import { parseActivity, ACTIVITY_HEADINGS } from '../utils/activityFormat';
import RaCloneModal from '../components/RaCloneModal';
import BlockRowsTable from '../components/BlockRowsTable';

const TABS = [
  { key: 'blocks',    label: 'Hazard Blocks' },
  { key: 'templates', label: 'RA Templates' },
];

export default function RiskAssessmentLibrary() {
  const navigate = useNavigate();
  const { isAdmin, capabilities } = useDepartment();
  // PER-PRODUCTION library authoring (writes to ra_blocks / ra_templates,
  // which live in a production). Gated on canAuthorRiskAssessments in the
  // ACTIVE department, mirroring the server's requireDepartmentLibraryAuthor
  // (tranche 3 increment 5 — was the unscoped `isBuyer || activeRole ===
  // 'supervisor'`). Admin keeps this for fork-Edit oversight, but the create
  // buttons (which require a production) are additionally hidden from admin via
  // canAuthorPerProd below.
  const canAuthor = isAdmin || capabilities.canAuthorRiskAssessments === true;
  // Per-production create CTAs (new bespoke block, add template) need an
  // active production to land in — admin has none, so hide from them. Admin
  // gets the platform-library buttons instead (added separately for Phase 3).
  const canAuthorPerProd = canAuthor && !isAdmin;
  // Used to gate the "Edit" button on forks — only the creator (or canAuthor
  // roles) can edit. Parsed from localStorage to match the rest of the app's
  // identity convention; sfx_user_id is set on every successful login.
  const currentUserId = Number(localStorage.getItem('sfx_user_id')) || null;

  // Always land on the Templates tab on each page load. The user can switch
  // to Blocks within the session, but reloads or revisits reset to Templates
  // (templates are the more common entry-point — pre-built collections that
  // most users start from).
  // 🔀 2026-10-13: a browser Back/Forward returns to the tab its history entry
  // was on (`hooks/useViewInHistory`); a load or a revisit by a link is unchanged.
  const [tab, setTab] = useHistoryState('ra-library-tab', 'templates', (v) => v === 'templates' || v === 'blocks');
  const [blocks, setBlocks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expand state per tab. Keys are `${kind}:${id}` (e.g. "seed:1") so the
  // same numeric id can't collide across kinds (e.g. seed:3 vs fork:3).
  const [expandedKey, setExpandedKey] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  // Per-category expanded state for the Blocks tab (v1.2: ~62 atomic
  // hazards grouped by category, e.g. "Wind", "Pyro"). Stored as
  // `expandedCategories` — present-and-truthy means expanded. Categories
  // absent from the map render collapsed. State is in-memory only: every
  // fresh page load resets to `{}` so all categories load collapsed
  // (intentional — the library has 10+ categories and an all-expanded
  // first-paint is overwhelming on phones).
  const [expandedCategories, setExpandedCategories] = useState({});
  const toggleCategory = (cat) => {
    setExpandedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleTabChange = (next) => {
    setTab(next);
    // Intentionally not persisted — every fresh page load returns to Templates.
    setExpandedKey(null);
  };

  // Clone modal state (Templates tab actions).
  const [cloneModalTarget, setCloneModalTarget] = useState(null);
  const [busyClone, setBusyClone] = useState(false);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, t] = await Promise.all([
        apiGet('/api/risk-assessments/blocks'),
        apiGet('/api/risk-assessments/templates'),
      ]);
      if (!b.ok) throw new Error('Failed to load blocks');
      if (!t.ok) throw new Error('Failed to load templates');
      setBlocks(await b.json());
      setTemplates(await t.json());
    } catch (err) {
      setError(err.message || 'Failed to load risk assessment library');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  // Template-row actions (Templates tab only).
  const handleEditTemplate = (template) => {
    // Forks only — seed templates aren't editable (they're CrewForge defaults).
    if (template.kind !== 'fork') return;
    navigate(`/risk-assessments/library/templates/edit/fork/${template.id}`);
  };

  const handleCloneConfirm = async (name) => {
    if (!cloneModalTarget) return;
    const { template } = cloneModalTarget;
    setBusyClone(true);
    try {
      const res = await apiPost(
        `/api/risk-assessments/templates/${template.kind}/${template.id}/clone`,
        { name },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to clone template');
        setBusyClone(false);
        return;
      }
      setCloneModalTarget(null);
      setBusyClone(false);
      await fetchLists();
    } catch (err) {
      alert(err.message || 'Failed to clone template');
      setBusyClone(false);
    }
  };

  const handleDeleteBlock = async (block) => {
    if (block.kind !== 'fork') return; // seeds cannot be deleted
    if (!window.confirm(`Delete the customised block "${block.name}"? This cannot be undone.`)) return;
    try {
      const res = await apiDelete(`/api/risk-assessments/blocks/fork/${block.id}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to delete block');
        return;
      }
      await fetchLists();
    } catch (err) {
      alert(err.message || 'Failed to delete block');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (template.kind !== 'fork') return; // seeds cannot be deleted
    if (!window.confirm(`Delete the custom template "${template.name}"? This cannot be undone.`)) return;
    try {
      const res = await apiDelete(`/api/risk-assessments/templates/fork/${template.id}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to delete template');
        return;
      }
      // Close the expanded panel since the template is gone.
      setExpandedKey(null);
      await fetchLists();
    } catch (err) {
      alert(err.message || 'Failed to delete template');
    }
  };

  const handleUseAsNewRa = (template) => {
    navigate(
      `/risk-assessments/new?source=template&kind=${template.kind}&id=${template.id}&title=${encodeURIComponent(template.name)}`,
    );
  };

  const expand = async (item) => {
    const key = `${item.kind}:${item.id}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (!detailCache[key]) {
      setDetailLoading(true);
      try {
        const endpoint = tab === 'blocks'
          ? `/api/risk-assessments/blocks/${item.kind}/${item.id}`
          : `/api/risk-assessments/templates/${item.kind}/${item.id}`;
        const res = await apiGet(endpoint);
        if (res.ok) {
          const data = await res.json();
          setDetailCache((prev) => ({ ...prev, [key]: data }));
        }
      } catch (err) {
        console.error('[RA library] detail fetch failed:', err);
      }
      setDetailLoading(false);
    }
  };

  const list = tab === 'blocks' ? blocks : templates;
  const countLabel = tab === 'blocks' ? 'hazard rows' : 'blocks';
  const countField = tab === 'blocks' ? 'row_count' : 'block_count';

  return (
    <div className="px-4 sm:px-6 py-4 max-w-[96rem] mx-auto">
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Risk Assessment Library</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            CrewForge default Blocks and Templates. Click any item to inspect its hazard rows.
          </p>
        </div>
        {canAuthorPerProd && tab === 'blocks' && (
          <button
            onClick={() => navigate('/risk-assessments/library/blocks/new')}
            className={BTN_XS.primary}
            data-testid="ra-library-new-block"
          >
            + New bespoke block
          </button>
        )}
        {/* Admin-only CrewForge platform-library authoring (Phase 3 — writes
            to seed_blocks / seed_templates, visible to every customer org on
            next page load). */}
        {isAdmin && tab === 'blocks' && (
          <button
            onClick={() => navigate('/risk-assessments/library/seed-blocks/new')}
            className={BTN_XS.primary}
            data-testid="ra-library-new-seed-block"
            title="Add a new CrewForge default block — visible to every customer"
          >
            + New CrewForge block
          </button>
        )}
        {isAdmin && tab === 'templates' && (
          <button
            onClick={() => navigate('/risk-assessments/library/seed-templates/new')}
            className={BTN_XS.primary}
            data-testid="ra-library-new-seed-template"
            title="Add a new CrewForge default template — visible to every customer"
          >
            + New CrewForge template
          </button>
        )}
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-indigo-600 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
            data-testid={`ra-library-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage error={error} />}

      {!loading && !error && list.length === 0 && tab === 'blocks' && (
        <EmptyState
          size="md"
          title="No hazard blocks in this library"
          description="Blocks appear here once the seed library has loaded. If this persists, ask an admin to re-run the seeder."
        />
      )}

      {!loading && !error && list.length === 0 && tab === 'templates' && (
        <EmptyState
          size="md"
          title="No templates available"
          description="Templates are pre-built collections of hazard blocks. The seed library normally provides a starter set."
        />
      )}

      {!loading && !error && list.length > 0 && tab === 'templates' && (
        <div className="space-y-2">
          {list.map((item) => renderItem(item, {
            tab, countField, countLabel, expandedKey, expand,
            detailCache, detailLoading, canAuthor, isAdmin, navigate, currentUserId,
            onEditTemplate: handleEditTemplate,
            onCloneTemplate: (template) => setCloneModalTarget({ template }),
            onUseAsNewRa: handleUseAsNewRa,
            onDeleteBlock: handleDeleteBlock,
            onDeleteTemplate: handleDeleteTemplate,
          }))}
        </div>
      )}

      {!loading && !error && list.length > 0 && tab === 'blocks' && (() => {
        // Group blocks by category for the Blocks tab.
        const groups = {};
        for (const b of list) {
          const cat = b.category || '__uncategorised__';
          if (!groups[cat]) groups[cat] = [];
          groups[cat].push(b);
        }
        // Sort categories alphabetically (Uncategorised always last); blocks
        // within a category alphabetically by name.
        const categoryKeys = Object.keys(groups).sort((a, b) => {
          if (a === '__uncategorised__') return 1;
          if (b === '__uncategorised__') return -1;
          return a.localeCompare(b);
        });
        for (const k of categoryKeys) groups[k].sort((a, b) => a.name.localeCompare(b.name));

        return (
          <div className="space-y-3">
            {categoryKeys.map((cat) => {
              const items = groups[cat];
              const collapsed = !expandedCategories[cat];
              const displayName = cat === '__uncategorised__' ? 'Uncategorised' : titleCaseCategory(cat);
              return (
                <div key={cat} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(cat)}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                    data-testid={`ra-library-category-${cat}`}
                    aria-expanded={!collapsed}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-700 dark:text-gray-200 text-sm">{displayName}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({items.length})</span>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {!collapsed && (
                    <div className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/50">
                      {items.map((item) => renderItem(item, {
                        tab, countField, countLabel, expandedKey, expand,
                        detailCache, detailLoading, canAuthor, isAdmin, navigate, currentUserId,
                        inGroup: true,
                        onDeleteBlock: handleDeleteBlock,
                      }))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      <RaCloneModal
        open={!!cloneModalTarget}
        title="Duplicate template"
        label="New template name"
        defaultValue={cloneModalTarget ? `${cloneModalTarget.template.name} — Copy` : ''}
        confirmLabel="Duplicate"
        busy={busyClone}
        onCancel={() => { if (!busyClone) setCloneModalTarget(null); }}
        onConfirm={handleCloneConfirm}
      />
    </div>
  );
}

// Renders a single block/template row (with its expand-to-detail body). Shared
// between the flat Templates tab and the grouped Blocks tab.
function renderItem(item, ctx) {
  const { tab, countField, countLabel, expandedKey, expand, detailCache,
          detailLoading, canAuthor, isAdmin, navigate, inGroup, currentUserId,
          onEditTemplate, onCloneTemplate, onUseAsNewRa, onDeleteBlock,
          onDeleteTemplate } = ctx;
  const key = `${item.kind}:${item.id}`;
  const isExpanded = expandedKey === key;
  const detail = detailCache[key];
  // When an item is expanded inside a category group, lift it out of the
  // shared divide-y stack so it reads as a card on its own — rounded
  // corners, ring, margin breathing room top and bottom. For standalone
  // items (Templates tab), upgrade the existing card to a "highlighted"
  // state when expanded.
  const wrapperClass = inGroup
    ? (isExpanded
      ? 'my-2 mx-2 rounded-md ring-1 ring-gray-300 dark:ring-slate-500 bg-gray-50 dark:bg-slate-700 overflow-hidden shadow-sm !border-t-0'
      : '')
    : (isExpanded
      ? 'rounded-lg ring-1 ring-gray-300 dark:ring-slate-500 bg-gray-50 dark:bg-slate-700 shadow-sm'
      : 'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800');
  // Clone + Create RA from template are open to any signed-in user. Edit
  // is restricted further (see below) — only canAuthor or the fork's creator.
  const showTemplateActions = tab === 'templates';
  const canEditThisFork = item.kind === 'fork' && (canAuthor || item.created_by === currentUserId);
  // Title-row tint when expanded — clearly lighter than the wrapper so the
  // header reads as a distinct strip. Hover is suppressed when expanded.
  // Mobile: stack toggle row above action buttons (otherwise the 3 BTN_XS
  // action buttons starve the title row to ~80px and the name `truncate`s to
  // empty). Desktop: original single-row layout preserved via md: overrides.
  const titleRowClass = isExpanded
    ? 'w-full flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 px-4 py-3 bg-gray-200 dark:bg-slate-500 border-b border-gray-300 dark:border-slate-400'
    : 'w-full flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors';
  // Reused on every mobile action button — Apple HIG/WCAG 2.5.5 44px minimum.
  const mobileTouchClass = 'min-h-[44px] md:min-h-0 inline-flex items-center justify-center';
  return (
    <div key={key} className={wrapperClass}>
      <div className={titleRowClass}>
        <button
          type="button"
          onClick={() => expand(item)}
          className="w-full md:w-auto md:flex-1 min-w-0 flex items-start md:items-center justify-between gap-3 text-left"
          data-testid={`ra-library-item-${item.seed_slug || item.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 md:flex-wrap md:gap-x-2 md:gap-y-1">
              <span className="font-medium text-gray-900 dark:text-gray-100 flex-1 min-w-0 truncate" title={item.name}>{item.name}</span>
              {/* Desktop-only badges — inline with the title. Mobile counterparts
                  live in the right-side div so they anchor in the corner.

                  A fork with seed_slug set is a per-production copy of a CF
                  default (auto-created on template clone) — semantically still
                  the CF hazard, the user didn't author the content. Only treat
                  a fork as "Custom" when seed_slug is null (truly bespoke). */}
              {(item.kind === 'seed' || (item.kind === 'fork' && item.seed_slug)) && (
                <span className="hidden md:inline-block shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                  CrewForge template
                </span>
              )}
              {item.kind === 'fork' && !item.seed_slug && (
                <span className="hidden md:inline-block shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  Custom
                </span>
              )}
              {!!item.is_personal && (
                <span className="hidden md:inline-block shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title="Saved in your personal workspace — visible only to you">
                  Personal
                </span>
              )}
            </div>
            {item.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{item.description}</p>
            )}
            {/* Mobile: count drops below the description so the name + badge
                get the full title-row width. Hidden on desktop where the
                count lives in the right-side flex below. */}
            <p className="md:hidden text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item[countField]} {countLabel}</p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 text-xs text-gray-500 dark:text-gray-400 shrink-0">
            {/* Mobile-only badges — pinned to the right edge alongside the
                chevron so every card has the badge in the same corner. Same
                seed_slug check as the desktop badge above. */}
            {(item.kind === 'seed' || (item.kind === 'fork' && item.seed_slug)) && (
              <span
                className="md:hidden text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                title="CrewForge template"
              >
                CT
              </span>
            )}
            {item.kind === 'fork' && !item.seed_slug && (
              <span className="md:hidden text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Custom
              </span>
            )}
            {!!item.is_personal && (
              <span className="md:hidden text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title="Personal workspace">
                Personal
              </span>
            )}
            <span className="hidden md:inline">{item[countField]} {countLabel}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        {showTemplateActions && (
          <div className="w-full md:w-auto grid grid-cols-2 md:flex md:items-center gap-2 md:shrink-0 md:ml-3">
            {canEditThisFork && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEditTemplate?.(item); }}
                className={`${BTN_XS.secondary} ${mobileTouchClass} col-span-2 md:col-span-1`}
                data-testid={`ra-library-template-edit-${item.kind}-${item.id}`}
              >
                Edit
              </button>
            )}
            {/* Site admin cannot create RAs nor host forks (no production
                or department), so these two CTAs are hidden for them. To
                create or duplicate on behalf of a user, admin impersonates
                via the People panel first. The Edit button above stays for
                admin so they can still author/modify forks for library
                content. */}
            {!isAdmin && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCloneTemplate?.(item); }}
                className={`${BTN_XS.secondary} ${mobileTouchClass}`}
                data-testid={`ra-library-template-clone-${item.kind}-${item.id}`}
                title="Make a copy of this template definition you can rename and tweak"
              >
                Duplicate template
              </button>
            )}
            {!isAdmin && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onUseAsNewRa?.(item); }}
                className={`${BTN_XS.primary} ${mobileTouchClass}`}
                data-testid={`ra-library-template-use-${item.kind}-${item.id}`}
                title="Materialise this template as a new Risk Assessment ready to fill in"
              >
                Create RA from template
              </button>
            )}
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
          {detailLoading && !detail && (
            <div className="py-4"><LoadingSpinner /></div>
          )}
          {detail && tab === 'blocks' && (
            <>
              <BlockRowsTable rows={detail.rows} />
              {/* Admin viewing a seed block: "Edit CrewForge default" goes to
                  the seed-block editor (writes the platform library directly).
                  Buyers/supervisors viewing a seed: "Customise (fork)" creates
                  a per-prod fork they can edit. Admin viewing a fork: nothing
                  (per-prod forks are buyer/supervisor territory; admin uses
                  impersonation if they need to touch one). */}
              {isAdmin && item.kind === 'seed' && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => navigate(`/risk-assessments/library/seed-blocks/edit/${item.id}`)}
                    className={BTN_XS.primary}
                    data-testid={`ra-library-edit-seed-block-${item.id}`}
                    title="Edit this CrewForge default — changes ripple to every customer on next page load"
                  >
                    Edit CrewForge default
                  </button>
                </div>
              )}
              {!isAdmin && canAuthor && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => navigate(`/risk-assessments/library/blocks/edit/${item.kind}/${item.id}`)}
                    className={BTN_XS.primary}
                    data-testid={`ra-library-edit-${item.kind}-${item.id}`}
                  >
                    {item.kind === 'seed' ? 'Customise (fork to my department)' : 'Edit'}
                  </button>
                  {item.kind === 'fork' && onDeleteBlock && (
                    <button
                      onClick={() => onDeleteBlock(item)}
                      className={BTN_XS['danger-soft']}
                      data-testid={`ra-library-block-delete-${item.id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
              {/* Regular users (no curator role) still get a Delete button on
                  their OWN personal-workspace entries. Server allows it via
                  assertCanEditFork's personal-space carve-out. */}
              {!isAdmin && !canAuthor && item.is_personal
                && item.kind === 'fork'
                && item.created_by === currentUserId
                && onDeleteBlock && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => onDeleteBlock(item)}
                    className={BTN_XS['danger-soft']}
                    data-testid={`ra-library-block-delete-${item.id}`}
                  >
                    Delete
                  </button>
                </div>
              )}
            </>
          )}
          {detail && tab === 'templates' && (
            <>
              <TemplateActivityPreview activity={detail.activity} />
              <div className="mt-4 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Hazards</div>
              <TemplateBlockList blocks={detail.blocks} />
              {/* Admin viewing a seed template: "Edit CrewForge default" goes
                  to the seed-template editor (writes the platform library
                  directly — visible to every user on next page load). */}
              {isAdmin && item.kind === 'seed' && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => navigate(`/risk-assessments/library/seed-templates/edit/${item.id}`)}
                    className={BTN_XS.primary}
                    data-testid={`ra-library-edit-seed-template-${item.id}`}
                    title="Edit this CrewForge default template — changes ripple to every customer on next page load"
                  >
                    Edit CrewForge default
                  </button>
                </div>
              )}
              {/* Delete is only available on custom (fork) templates the user
                  is authorised to edit. Seed templates can't be deleted. */}
              {item.kind === 'fork' && canEditThisFork && onDeleteTemplate && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => onDeleteTemplate(item)}
                    className={BTN_XS['danger-soft']}
                    data-testid={`ra-library-template-delete-${item.id}`}
                  >
                    Delete template
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Read-only preview of the four-section Activity prose seeded on each template.
// Surfaces the same example a user would get in the Activity field of an RA
// they materialise from this template. Hides itself entirely when the
// template has no activity text (e.g. a freshly-cloned fork the user hasn't
// filled in yet) so the panel stays clean.
function TemplateActivityPreview({ activity }) {
  if (!activity || !activity.trim()) return null;
  const fields = parseActivity(activity);
  const hasAny = ACTIVITY_HEADINGS.some((h) => (fields[h.key] || '').trim());
  if (!hasAny) return null;
  return (
    <div className="mt-3 mb-4 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Example activity</div>
      <dl className="space-y-2.5">
        {ACTIVITY_HEADINGS.map((h) => {
          const val = (fields[h.key] || '').trim();
          if (!val) return null;
          return (
            <div key={h.key}>
              <dt className="text-xs font-semibold text-gray-700 dark:text-gray-200">{h.label}</dt>
              <dd className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{val}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function TemplateBlockList({ blocks }) {
  if (!blocks || blocks.length === 0) {
    return <p className="py-3 text-sm text-gray-500 dark:text-gray-400">No blocks yet.</p>;
  }
  // Polymorphic block shape (post-Task 4): each entry has `join_id`, `kind`
  // ('seed'|'fork'), `seed_slug`, `name`, plus seed_block_id/ra_block_id for
  // its kind. Older builds returned `seed_block_slug`; keep that as a
  // fallback so any pre-migration cached responses still render.
  return (
    <ol className="mt-3 space-y-2 md:space-y-1.5">
      {blocks.map((b, i) => {
        const slug = b.seed_slug || b.seed_block_slug || null;
        const stableKey = b.join_id != null ? `j${b.join_id}` : (slug || `i${i}`);
        return (
          <li
            key={`${stableKey}:${i}`}
            className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-200"
          >
            <span className="shrink-0 mt-0.5 inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300">
              {i + 1}
            </span>
            {/* Mobile: title above slug, stacked. Desktop: inline side-by-side. */}
            <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:gap-3">
              <span className="font-medium">{b.name || slug}</span>
              {slug && b.name && (
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-0.5 md:mt-0">{slug}</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
